import { createSelector } from 'reselect';
import { applyToParentAndAllSubReducers } from '../../../reducers/subReducerHelpers';
import { toDecimals } from '../../../helpers/currency';
import { getSeatsRange } from '../../../../helpers/seatsRange';
import { buildPriceLevelQuantityArray } from '../reducers/ticket-picker';

const getTickets = state => state.tickets.tickets;
const getPriceLevelState = state => state.priceLevel;
const appliedTaggableDiscountCode = state => state?.couponTag?.appliedTaggableDiscountCode;

const isSeatedTicket = ticket => {
  return !!ticket?.is_seated;
};

/**
 * Adds a ticket's seat info into the shared deepSeatMap, grouping seats by
 * event/price-level and then by section/row/properties, and returns the
 * grouped seat infos for the ticket's price level.
 */
const buildGroupedSeatInfos = (ticket, deepSeatMap) => {
  const seatInfo = ticket.seat_info;
  const priceLevelId = [ticket.price_level_id, ticket.listing_upsell_id].filter(Boolean).join('-');

  // Include the ticket's actual (post-discount) cost so seats belonging to
  // discounted tickets are grouped separately from full-price tickets of the
  // same price level, mirroring how the price-level entries are split.
  const seatMapKey = `${ticket.event_id}-${priceLevelId}-${ticket.cost.amount}`;
  if (!deepSeatMap.has(seatMapKey)) {
    deepSeatMap.set(seatMapKey, new Map());
  }
  const seatMap = deepSeatMap.get(seatMapKey);

  if (seatInfo.seat) {
    const properties = (seatInfo.seatProperties || []).join('-');
    const key = `${seatInfo.section}-${seatInfo.row}-${properties}`;
    if (!seatMap.has(key)) {
      seatMap.set(key, {
        section: seatInfo.section,
        row: seatInfo.row,
        seat: [seatInfo.seat],
        isTableSeat: seatInfo.isTableSeat,
        seatProperties: seatInfo.seatProperties,
      });
    } else {
      seatMap.get(key).seat.push(seatInfo.seat);
    }
    seatMap.get(key).seats_info = getSeatsRange(seatMap.get(key).seat);
  }

  return Array.from(seatMap.values());
};

/**
 * Combines all tickets into groups organized by event and then price level and then by listing upsell ID
 * End object is of the form:
 *  {
 *      <event_id>:[{price_level_id: <id>...}, {price_level_id: <id2>...}...],
 *      <event_id2>:[{price_level_id: <id>...}, {price_level_id: <id2>...}...]
 *  }
 */
export const getPriceLevelSelectionsByEvent = createSelector(
  [getTickets, appliedTaggableDiscountCode],
  (tickets, appliedTaggableDiscountCode) => {
    const levels = {};
    const deepSeatMap = new Map();
    for (let i = 0; i < tickets.length; i++) {
      if (!levels[tickets[i].event_id]) {
        levels[tickets[i].event_id] = [];
      }

      const isSeated = isSeatedTicket(tickets[i]);
      const isPartOfTimeslotBuyout = !!tickets[i]?.is_tsb;
      const useCostAmount = isSeated || isPartOfTimeslotBuyout
      const useCostAmountOrPriceLevel = (appliedTaggableDiscountCode && !!tickets[i].event_is_pyos) ? false : useCostAmount;

      const costAmount = tickets[i].cost.amount;
      const priceLevelCostAmount = tickets[i].price_level_cost.amount
      const amount = useCostAmountOrPriceLevel ? costAmount : priceLevelCostAmount

      // if we've already added a ticket that matches this one, just increase the quantity, subtotal, etc.
      // match by price level ID, cost, and listing upsell ID

      let found = false;
      for (let j = 0; j < levels[tickets[i].event_id].length; j++) {
        let sameName = false;
        // Group by the ticket's actual (post-discount) cost so that tickets of the
        // same price level split into separate groups when a discount is applied to
        // only some of them, while identically-priced tickets still merge.
        const samePrice = +levels[tickets[i].event_id][j].unit_cost === +tickets[i]?.cost?.amount;

        const normalizedName = levels[tickets[i].event_id][j]?.name?.replace(/\s+/g, '_').toLowerCase() ?? '';
        const entryNormalizedName = tickets[i]?.price_level_name?.replace(/\s+/g, '_').toLowerCase() ?? '';
        sameName = normalizedName === entryNormalizedName;

        const checkLevelAndUpsellId =
          levels[tickets[i].event_id][j].price_level_id === tickets[i].price_level_id &&
          levels[tickets[i].event_id][j].listing_upsell_id === tickets[i].listing_upsell_id;

        if (checkLevelAndUpsellId && samePrice && sameName || isPartOfTimeslotBuyout) {
          levels[tickets[i].event_id][j].total_price += tickets[i].cost.amount;
          levels[tickets[i].event_id][j].total_fee += tickets[i].buyer_fee.amount;
          levels[tickets[i].event_id][j].subtotal += amount + tickets[i].buyer_fee.amount;
          levels[tickets[i].event_id][j].quantity++;

          //seats group by row
          if (isSeated) {
            levels[tickets[i].event_id][j].groupedSeatInfos = buildGroupedSeatInfos(tickets[i], deepSeatMap);
          }
          found = true;
          break;
        }
      }

      if (!found) {
        let _seat_info = null;
        let groupedSeatInfos = null;
        if (isSeated) {
          _seat_info = tickets[i].seat_info;
          _seat_info.seats = [_seat_info.seat];
          _seat_info.seats_info = _seat_info.seat;

          groupedSeatInfos = buildGroupedSeatInfos(tickets[i], deepSeatMap);
        }

        const priceLevelEntry = {
          price_level_id: tickets[i].price_level_id,
          name: tickets[i].price_level_name,
          price: tickets[i].price_level_cost.amount,
          unit_cost: tickets[i].cost.amount,
          total_price: tickets[i].cost.amount,
          fee: tickets[i].buyer_fee.amount,
          total_fee: tickets[i].buyer_fee.amount,
          quantity: 1,
          subtotal: amount + tickets[i].buyer_fee.amount,
          currency: tickets[i].cost.currency,
          currency_symbol: tickets[i].cost.symbol,
          event_id: tickets[i].event_id,
          quantities: buildPriceLevelQuantityArray(
            tickets[i]?.price_level_min_qty ?? 0,
            tickets[i]?.price_level_max_qty ?? 10,
            tickets[i]?.price_level_inc ?? 1,
          ),
          description: tickets[i].price_level_description,
          listing_upsell_id: tickets[i].listing_upsell_id,
          is_seated: isSeated,
          is_tsb: !!tickets[i]?.is_tsb,
          rows: isSeated ? [_seat_info] : [],
          groupedSeatInfos: isSeated ? groupedSeatInfos : [],
        };
        if (!priceLevelEntry.is_seated) {
          priceLevelEntry.price = tickets[i]?.price_level_cost?.amount;
        }
        levels[tickets[i].event_id].push(priceLevelEntry);
      }
    }
    return levels;
  }
);

export const getPriceLevelsWithUnmetMinimums = createSelector(
  [getTickets],
  (tickets) => {
    const levelsWithMin = {};
    const quantitiesByPriceLevelId = {};
    tickets.forEach(ticket => {
      if (ticket?.price_level_id) {
        if (!quantitiesByPriceLevelId[ticket.price_level_id]) {
          quantitiesByPriceLevelId[ticket.price_level_id] = 0;
        }
        quantitiesByPriceLevelId[ticket.price_level_id]++;
        if (ticket?.price_level_min_qty) {
          levelsWithMin[ticket.price_level_id] = {
            price_level_id: ticket.price_level_id,
            name: ticket.price_level_name,
            minimum_purchase_limit: +ticket.price_level_min_qty,
          };
        }
      }
    });
    return Object.values(levelsWithMin).filter(lvl => {
      return (
        !!lvl?.minimum_purchase_limit &&
        +lvl.minimum_purchase_limit > 0 &&
        !!quantitiesByPriceLevelId[lvl.price_level_id] &&
        +quantitiesByPriceLevelId[lvl.price_level_id] < +lvl.minimum_purchase_limit
      );
    });
  }
);

export const getNumTicketsByLevel = upsellId => {
  return createSelector(
    [getTickets],
    tickets => {
      let levels = {};
      for (let i = 0; i < tickets.length; i++) {
        if (upsellId && +tickets[i].listing_upsell_id !== +upsellId) {
          continue;
        }
        if (!levels[tickets[i].price_level_id]) {
          levels[tickets[i].price_level_id] = 1;
        } else {
          levels[tickets[i].price_level_id]++;
        }
      }
      return levels;
    }
  );
};

/**
 * Get all loaded price levels including those loaded in price levels sub reducers
 */
export const getAllLoadedPriceLevels = createSelector(
  [getPriceLevelState],
  priceLevelState => {
    return getAllLoadedPriceLevelsFromPriceLevelState(priceLevelState);
  }
);
const getAllLoadedPriceLevelsFromPriceLevelState = priceLevelState => {
  const levels = {};
  applyToParentAndAllSubReducers({ ...priceLevelState }, state => {
    if (typeof state.levels === 'object' && state.levels !== null) {
      Object.keys(state.levels).forEach(levelKey => {
        if (levels[levelKey] === undefined) {
          levels[levelKey] = state.levels[levelKey];
        }
      });
    }
    return state;
  });
  return levels;
};

export const getTicketsOfLevel = (ticketsState, levelId) => {
  let result = [];
  if (Array.isArray(ticketsState?.tickets)) {
    ticketsState.tickets.forEach(ticket => {
      if (+ticket?.price_level_id === +levelId) {
        result.push(ticket);
      }
    });
  }
  return result;
};

export const hasTicketsCartedForLevel = (ticketsState, levelId) => {
  return !!getTicketsOfLevel(ticketsState, levelId).length;
};

export const levelIsPWYW = (priceLevelState, levelId) => {
  return (
    typeof priceLevelState?.levels === 'object' &&
    priceLevelState.levels[levelId] !== undefined &&
    !!priceLevelState.levels[levelId]?.pwyw
  );
};

export const getPWYWPriceForPriceLevel = (priceLevelState, ticketPickerState, ticketsState, levelId) => {
  let price = null;

  if (levelIsPWYW(priceLevelState, levelId)) {
    const priceLevel = priceLevelState.levels[levelId];

    // if the user already carted pwyw tickets for this level, use the price on the tickets
    if (Array.isArray(ticketsState?.tickets)) {
      ticketsState.tickets.forEach(ticket => {
        if (+ticket?.price_level_id === +levelId && !!ticket?.cost) {
          price = toDecimals({ currency: ticket.cost?.currency ?? 'USD', amount: +ticket.cost.amount });
        }
      });
    }

    // check if the user set a price for the level
    if (
      price === null &&
      typeof ticketPickerState?.pwywPrices === 'object' &&
      ticketPickerState.pwywPrices[levelId] !== undefined
    ) {
      price = +ticketPickerState.pwywPrices[levelId];
    }

    // otherwise check for the default price to show the user
    if (price === null) {
      price = toDecimals({ currency: priceLevel?.price?.currency ?? 'USD', amount: +priceLevel?.price?.price });
    }
  }

  return price;
};
